import PropTypes from 'prop-types';
import { Box, MenuItem, FormControl, InputLabel, Select, Typography } from '@mui/material';
import BaseModal from 'components/BaseModal';
import ProfileService from 'services/ProfileService';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';
import useStudyFields from 'hooks/useStudyFields';
import useRoles from 'hooks/useRoles';
import { useEffect, useState, useCallback } from 'react';

const Props = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function ProfileModal({ open, onClose }) {
  const profileService = new ProfileService();
  const clevertap = getCleverTap();
  const { user, profile, updateUser } = useAuthUser();
  const studyFields = useStudyFields();
  const roles = useRoles();
  const [form, setForm] = useState({
    idStudyField: '',
    idRole: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const ctEvent = useCallback(
    (name) => {
      const studyField = studyFields.find((field) => field.id === form.idStudyField);
      const role = roles.find((role) => role.id === form.idRole);

      clevertap.event.push(name, {
        Identity: user.id,
        Product: 'MTG',
        Email: user.email,
        URL: '/workspace/',
        Description: name,
        Locale: 'workspace',
        'Study Field': studyField?.name ?? '',
        Role: role?.name ?? '',
      });
    },
    [clevertap, user.id, user.email, studyFields, roles, form],
  );

  const handleSubmit = useCallback(async () => {
    setTouched(true);
    if (!form.idStudyField || !form.idRole) {
      return;
    }

    setSubmitting(true);
    try {
      await profileService.update(user.id, form);
      await updateUser();

      ctEvent('Profile form Submitted');

      onClose();
    } catch {
      //
    }
    setSubmitting(false);
  }, [form, profileService, user.id, updateUser, ctEvent, onClose]);

  useEffect(() => {
    if (open && studyFields.length > 0 && roles.length > 0) {
      const selectedField = studyFields.find((field) => field.id === profile.idStudyField);
      const role = roles.find((role) => role.id === profile.idRole);
      setForm({
        idStudyField: selectedField ? selectedField.id : '',
        idRole: role ? role.id : '',
      });

      ctEvent('Profile form Displayed');
    }
  }, [open, studyFields, roles]);

  return (
    <BaseModal
      size="sm"
      open={open}
      title={<b>Tell us about you</b>}
      content={
        <Box>
          <Typography variant="body1">
            This helps us personalize your experience and tailor recommendations
          </Typography>
          <Box textAlign="left">
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="study-field-select-label">Field of study</InputLabel>
              <Select
                labelId="study-field-select-label"
                id="study-field-select"
                value={form.idStudyField}
                label="Field of study"
                error={touched && !form.idStudyField}
                onChange={(e) => {
                  setForm((prevForm) => ({
                    ...prevForm,
                    idStudyField: e.target.value,
                  }));
                }}
              >
                {studyFields.map((field) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={form.idRole}
                label="Role"
                error={touched && !form.idRole}
                onChange={(e) => {
                  setForm((prevForm) => ({
                    ...prevForm,
                    idRole: e.target.value,
                  }));
                }}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      }
      actionButton={true}
      actionText="Save"
      isLoading={submitting}
      disableActionButton={submitting}
      onActionClick={handleSubmit}
      cancelButton={true}
      onClose={onClose}
    />
  );
}

ProfileModal.propTypes = Props;
